import { useRef, useEffect } from 'react';
import { GENERIC_SEARCH_ID } from '../constants/client-selection';
import { MatchmakingSearchInput } from './ProDashboardMatchmaking/MatchmakingSearchInput';
import { MatchmakingEmptyState } from './ProDashboardMatchmaking/MatchmakingEmptyState';
import { MatchmakingNoResultsState } from './ProDashboardMatchmaking/MatchmakingNoResultsState';
import { MatchmakingResultsSection } from './ProDashboardMatchmaking/MatchmakingResultsSection';
import { useMatchmakingState } from '../hooks/useMatchmakingState';
import { useLoadingMessages } from '../hooks/useLoadingMessages';
import { useImagePreloading } from '../hooks/useImagePreloading';
import { useMatchmakingComputed } from '../hooks/useMatchmakingComputed';
import { useCreateSearch, useFilteredProviders, useSaveSearchResults } from '../hooks/useMatchmaking';
import { useMatchSearch } from '../hooks/useMatchSearch';
import type { HistoryItem } from './MatchmakingHistoryPopover';
import type { StepperAnswers } from '../hooks/useMatchmakingStateManagement';

type GenericSearchTabProps = {
  state: ReturnType<typeof import('../hooks/useMatchmakingStateManagement').useMatchmakingStateManagement>;
  advisorProfileId: string | null;
  handleFindMatchesRef?: React.MutableRefObject<((answers: StepperAnswers) => void) | null>;
};

export const GenericSearchTab = ({ state, advisorProfileId, handleFindMatchesRef }: GenericSearchTabProps) => {
  const {
    isLoading,
    setIsLoading,
    isLoadingMore,
    setIsLoadingMore,
    showNoProvidersFound,
    setShowNoProvidersFound,
    noProviderMessage,
    setNoProviderMessage,
    loadMoreMessage,
    setLoadMoreMessage,
    isHistoryPopoverOpen,
    setHistoryPopoverOpen,
    showHistoryTooltip,
    setShowHistoryTooltip,
    isViewingHistoryResults,
    setIsViewingHistoryResults,
    shouldFetchHistory,
    setShouldFetchHistory,
    setIsSupportModalOpen,
    currentSearchId,
    setCurrentSearchId,
    loadCount,
    setLoadCount,
    selectedZipCode,
    selectedCounty,
    selectedAddress,
    selectedLatitude,
    selectedLongitude,
    selectedState,
    searchCounter,
    setSearchCounter,
    stepperAnswers,
    setStepperAnswers,
    lastPromptRef,
    setError,
    setIsLocationModalOpen,
  } = state;

  // Use matchmaking state with GENERIC_SEARCH_ID
  const matchmakingState = useMatchmakingState(GENERIC_SEARCH_ID);
  const matchingPrompt = matchmakingState.query;
  const apiResults = matchmakingState.matches;
  const hasMore = matchmakingState.hasMore;

  const { currentLoadingMessageIndex, currentLoadingTheme } = useLoadingMessages(isLoading);

  const {
    isPreloadingImages,
    preloadedImages,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
  } = useImagePreloading(apiResults, isLoading);

  const {
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
  } = useMatchmakingComputed({
    selectedClient: null, // No specific client for generic search
    selectedZipCode,
    selectedCounty,
    isViewingHistoryResults,
    searchCounter,
    isLoading,
    hasMore,
    loadCount,
    apiResults,
  });

  // Setup mutations for generic search
  const createSearchMutation = useCreateSearch();
  const filteredProvidersMutation = useFilteredProviders();
  const saveResultsMutation = useSaveSearchResults();

  // Setup executeMatchSearch for GENERIC_SEARCH_ID
  const { executeMatchSearch } = useMatchSearch({
    matchingPrompt,
    hasClients: true, // Always true for generic search
    selectedClientId: GENERIC_SEARCH_ID,
    advisorProfileId,
    stepperAnswers,
    apiResults,
    currentSearchId,
    loadCount,
    selectedState,
    selectedCounty,
    selectedAddress,
    selectedZipCode: selectedZipCode || '',
    selectedLatitude,
    selectedLongitude,
    clientProfile: undefined, // No client profile for generic search
    matchmakingState,
    isViewingHistoryResults,
    createSearchMutation,
    filteredProvidersMutation,
    saveResultsMutation,
    setIsLoading,
    setIsLoadingMore,
    setError,
    setShowNoProvidersFound,
    setNoProviderMessage,
    setLoadMoreMessage,
    setIsLocationModalOpen,
    setCurrentSearchId,
    setLoadCount,
    setSearchCounter,
    setIsViewingHistoryResults,
    setShouldFetchHistory,
    lastPromptRef,
  });

  const handleSelectHistory = (item: HistoryItem) => {
    matchmakingState.setClientQuery(item.promptText);
  };

  const handlePromptChange = (value: string) => {
    matchmakingState.setClientQuery(value);
  };

  const handleGenerateMatches = () => {
    // For generic search, open the location modal directly
    if (!matchingPrompt.trim()) {
      return;
    }
    lastPromptRef.current = matchingPrompt;
    setIsLocationModalOpen(true);
  };

  // Store the handler in a ref so parent can access it without causing re-renders
  const handleFindMatchesInternal = useRef((answers: StepperAnswers) => {
    setStepperAnswers(answers);
    setIsLocationModalOpen(false);
    executeMatchSearch(false, answers);
  });

  // Update the ref whenever dependencies change
  useEffect(() => {
    handleFindMatchesInternal.current = (answers: StepperAnswers) => {
      setStepperAnswers(answers);
      setIsLocationModalOpen(false);
      executeMatchSearch(false, answers);
    };
  }, [executeMatchSearch, setStepperAnswers, setIsLocationModalOpen]);

  // Expose the handler to parent via the ref prop (no re-renders triggered)
  useEffect(() => {
    if (handleFindMatchesRef) {
      handleFindMatchesRef.current = (answers: StepperAnswers) => {
        handleFindMatchesInternal.current(answers);
      };
    }
  }, [handleFindMatchesRef]);

  return (
    <main className="w-full space-y-8 flex-1 min-w-0">
      {/* Header for generic search */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Generic Search</h2>
        <p className="text-gray-600">
          Search for service providers without selecting a specific client. Your search history will be saved
          separately.
        </p>
      </div>

      <MatchmakingSearchInput
        matchingPrompt={matchingPrompt}
        onPromptChange={handlePromptChange}
        onGenerateMatches={handleGenerateMatches}
        isLoading={isLoading}
        hasClients={true} // Always true for generic search
        isHistoryPopoverOpen={isHistoryPopoverOpen}
        setHistoryPopoverOpen={setHistoryPopoverOpen}
        showHistoryTooltip={showHistoryTooltip}
        setShowHistoryTooltip={setShowHistoryTooltip}
        onSelectHistory={handleSelectHistory}
        advisorId={advisorProfileId}
        clientId={GENERIC_SEARCH_ID}
        shouldFetchHistory={shouldFetchHistory && !!advisorProfileId}
        onFetchHistoryComplete={() => setShouldFetchHistory(false)}
      />

      {!isLoading && !hasMatches && !showNoProvidersFound && (
        <MatchmakingEmptyState />
      )}

      {showNoProvidersFound && noProviderMessage && (
        <MatchmakingNoResultsState />
      )}

      <MatchmakingResultsSection
        isLoading={isLoading}
        hasMatches={hasMatches}
        showNoProvidersFound={showNoProvidersFound}
        isPreloadingImages={isPreloadingImages}
        currentLoadingTheme={currentLoadingTheme}
        currentLoadingMessageIndex={currentLoadingMessageIndex}
        currentImagePreloadingTheme={currentImagePreloadingTheme}
        imagePreloadingMessageIndex={imagePreloadingMessageIndex}
        matchResults={matchResults}
        preloadedImages={preloadedImages}
        currentSearchId={currentSearchId}
        advisorId={advisorProfileId}
        clientId={GENERIC_SEARCH_ID}
        isViewingHistoryResults={isViewingHistoryResults}
        onHistoryDetailsClick={() => state.setIsHistoryDetailsModalOpen(true)}
        cheerfulMessage={cheerfulMessage}
        shouldShowPostSingleLoadExhaustedMessage={shouldShowPostSingleLoadExhaustedMessage}
        shouldShowPostSecondLoadMessage={shouldShowPostSecondLoadMessage}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreMessage={loadMoreMessage}
        onLoadMore={() => {
          // Load more functionality will be handled by parent
        }}
        onContactUs={() => setIsSupportModalOpen(true)}
      />
    </main>
  );
};
